import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../utils/api';
import { isWebAuthnSupported, startRegistration } from '../utils/webauthn';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Phone, CheckCircle, Clock, XCircle, Upload, Camera, FileText, TrendingUp, Send, Fingerprint, Trash2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [webauthnCreds, setWebauthnCreds] = useState([]);
  const [registeringBiometric, setRegisteringBiometric] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFADialogOpen, setTwoFADialogOpen] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    address: '',
    date_of_birth: '',
    occupation: '',
    annual_income: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name,
        phone: user.phone || '',
        address: user.address || '',
        date_of_birth: user.date_of_birth || '',
        occupation: user.occupation || '',
        annual_income: user.annual_income || ''
      });
    }
    fetchStats();
    setBiometricSupported(isWebAuthnSupported());
    fetchWebAuthnCreds();
    if (user) setTwoFAEnabled(!!user.totp_enabled);
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await api.getUserStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchWebAuthnCreds = async () => {
    try {
      const res = await api.getWebAuthnCredentials();
      setWebauthnCreds(res.data);
    } catch (error) {
      console.error('Failed to fetch webauthn creds:', error);
    }
  };

  const handleRegisterBiometric = async () => {
    setRegisteringBiometric(true);
    try {
      const optionsRes = await api.getWebAuthnRegisterOptions();
      const options = optionsRes.data;
      const credential = await startRegistration(options);
      await api.verifyWebAuthnRegister(credential);
      toast.success(t('profile_bio_success'));
      fetchWebAuthnCreds();
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error(t('profile_bio_cancel'));
      } else {
        toast.error(error.response?.data?.detail || t('profile_bio_fail'));
      }
    } finally {
      setRegisteringBiometric(false);
    }
  };

  const handleDeleteBiometric = async (credId) => {
    if (!window.confirm(t('profile_bio_delete_confirm'))) return;
    try {
      await api.deleteWebAuthnCredential(credId);
      toast.success(t('profile_bio_deleted'));
      fetchWebAuthnCreds();
    } catch (error) {
      toast.error(t('profile_bio_delete_fail'));
    }
  };

  const handleSetup2FA = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFASetup(res.data);
      setTwoFADialogOpen(true);
      setTwoFACode('');
    } catch (error) {
      toast.error(t('profile_2fa_setup_fail'));
    }
  };

  const handleVerify2FA = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/2fa/verify-setup?code=${twoFACode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('profile_2fa_verified'));
      setTwoFAEnabled(true);
      setTwoFADialogOpen(false);
      setTwoFASetup(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile_2fa_invalid_code'));
    }
  };

  const handleDisable2FA = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/2fa/disable?code=${disableCode}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('profile_2fa_disabled_msg'));
      setTwoFAEnabled(false);
      setDisableCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile_2fa_invalid_code'));
    }
  };

  const handleProfileUpdate = async () => {
    try {
      await api.updateProfile(formData);
      toast.success(t('profile_updated'));
      setEditing(false);
      window.location.reload();
    } catch (error) {
      toast.error(t('profile_update_fail'));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('profile_image_size'));
      return;
    }

    setUploading(true);
    try {
      await api.uploadProfileImage(file);
      toast.success(t('profile_image_updated'));
      window.location.reload();
    } catch (error) {
      toast.error(t('profile_image_fail'));
    } finally {
      setUploading(false);
    }
  };

  const getKYCStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, label: t('status_pending'), color: 'text-gray-600', bg: 'bg-gray-500/10' },
      submitted: { icon: Clock, label: t('submitted'), color: 'text-blue-600', bg: 'bg-blue-500/10' },
      approved: { icon: CheckCircle, label: t('approved'), color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
      rejected: { icon: XCircle, label: t('rejected'), color: 'text-red-600', bg: 'bg-red-500/10' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg}`}>
        <Icon className={`h-4 w-4 ${config.color}`} />
        <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl" data-testid="profile-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            {t('profile_title')}
          </h1>
          <p className="text-muted-foreground">
            {t('profile_account_info')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border/50 rounded-2xl p-6 sticky top-24">
              <div className="text-center mb-6">
                <div className="relative inline-block">
                  {user?.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-4 border-accent/20"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-accent/10 flex items-center justify-center border-4 border-accent/20">
                      <User className="h-12 w-12 text-accent" />
                    </div>
                  )}
                  <label
                    htmlFor="profile-image-upload"
                    className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center cursor-pointer hover:bg-accent/90 smooth-transition"
                    data-testid="upload-profile-image-button"
                  >
                    <Camera className="h-4 w-4" />
                  </label>
                  <input
                    id="profile-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
                <h2 className="font-heading text-xl font-semibold text-primary mt-4">{user?.full_name}</h2>
                <p className="text-muted-foreground text-sm">{user?.email}</p>
                <div className="mt-4">
                  {getKYCStatusBadge(user?.kyc_status)}
                </div>
              </div>

              <div className="space-y-3 pt-6 border-t border-border">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{user?.email}</span>
                </div>
                {user?.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{user?.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} {t('profile_registered_at')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="stat-total-transfers">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Send className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold font-heading text-primary">{stats.total_transfers}</div>
                  <div className="text-sm text-muted-foreground">{t('profile_total_transfers')}</div>
                </div>
                <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="stat-total-amount">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-emerald-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold font-heading text-primary">¥{stats.total_amount_jpy.toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground">{t('profile_total_amount')}</div>
                </div>
                <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="stat-recipients">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold font-heading text-primary">{stats.recipient_count}</div>
                  <div className="text-sm text-muted-foreground">{t('profile_total_recipients')}</div>
                </div>
              </div>
            )}

            {/* Profile Info */}
            <div className="bg-card border border-border/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-semibold text-primary">{t('profile_basic_info')}</h3>
                {!editing ? (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile-button">
                    {t('edit')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-button">
                      {t('cancel')}
                    </Button>
                    <Button size="sm" onClick={handleProfileUpdate} data-testid="save-profile-button">
                      {t('save')}
                    </Button>
                  </div>
                )}
              </div>

              {editing ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('name')}</Label>
                    <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} data-testid="edit-fullname-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('phone')}</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="edit-phone-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('profile_address')}</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} data-testid="edit-address-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('profile_dob')}</Label>
                    <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} data-testid="edit-dob-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('profile_occupation')}</Label>
                    <Input value={formData.occupation} onChange={(e) => setFormData({...formData, occupation: e.target.value})} data-testid="edit-occupation-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('profile_income')}</Label>
                    <Input type="number" value={formData.annual_income} onChange={(e) => setFormData({...formData, annual_income: e.target.value})} data-testid="edit-income-input" />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('name')}</div>
                    <div className="font-medium text-primary">{user?.full_name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('email')}</div>
                    <div className="font-medium text-primary">{user?.email}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('phone')}</div>
                    <div className="font-medium text-primary">{user?.phone || t('profile_not_set')}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('profile_address')}</div>
                    <div className="font-medium text-primary">{user?.address || t('profile_not_set')}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('profile_dob')}</div>
                    <div className="font-medium text-primary">{user?.date_of_birth || t('profile_not_set')}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('profile_occupation')}</div>
                    <div className="font-medium text-primary">{user?.occupation || t('profile_not_set')}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">{t('profile_income')}</div>
                    <div className="font-medium text-primary">
                      {user?.annual_income ? `${user.annual_income.toLocaleString()}${t('profile_annual_income_unit')}` : t('profile_not_set')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* KYC Documents */}
            <div className="bg-card border border-border/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-primary">{t('profile_kyc_docs')}</h3>
                <Button size="sm" variant="outline" onClick={() => window.location.href = '/kyc'} data-testid="go-to-kyc-button">
                  <FileText className="h-4 w-4 mr-2" />
                  {t('profile_go_kyc')}
                </Button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
                  <div>
                    <div className="font-medium text-primary mb-1">{t('profile_kyc_status_label')}</div>
                    <div className="text-sm text-muted-foreground">
                      {user?.kyc_status === 'pending' && t('profile_kyc_needed')}
                      {user?.kyc_status === 'submitted' && t('profile_kyc_under_review')}
                      {user?.kyc_status === 'approved' && t('profile_kyc_completed')}
                      {user?.kyc_status === 'rejected' && t('profile_kyc_rejected_msg')}
                    </div>
                  </div>
                  {getKYCStatusBadge(user?.kyc_status)}
                </div>
              </div>
            </div>

            {/* Recent Transfers */}
            {stats?.recent_transfers && stats.recent_transfers.length > 0 && (
              <div className="bg-card border border-border/50 rounded-2xl p-6">
                <h3 className="font-heading text-lg font-semibold text-primary mb-4">{t('profile_recent_transfers')}</h3>
                <div className="space-y-3">
                  {stats.recent_transfers.map((transfer) => (
                    <div key={transfer.id} className="flex items-center justify-between p-4 border border-border rounded-xl">
                      <div>
                        <div className="font-medium text-primary">¥{transfer.total_jpy.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(transfer.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{transfer.status}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Biometric Authentication Section */}
          {biometricSupported && (
            <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="biometric-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <Fingerprint className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-primary">{t('profile_bio_fingerprint')}</h3>
                  <p className="text-sm text-muted-foreground">{t('profile_bio_login_desc')}</p>
                </div>
              </div>

              {webauthnCreds.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {webauthnCreds.map((cred) => (
                    <div key={cred.id} className="flex items-center justify-between py-3 px-4 bg-secondary/30 rounded-xl" data-testid={`biometric-cred-${cred.id}`}>
                      <div className="flex items-center gap-3">
                        <Fingerprint className="h-5 w-5 text-emerald-600" />
                        <div>
                          <div className="text-sm font-medium text-primary">{cred.device_name || t('profile_bio_device_name')}</div>
                          <div className="text-xs text-muted-foreground">
                            {t('profile_biometric_registered')}: {new Date(cred.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => handleDeleteBiometric(cred.id)} data-testid={`delete-biometric-${cred.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-secondary/20 rounded-xl p-4 mb-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('profile_bio_none_desc')}
                  </p>
                </div>
              )}

              <Button onClick={handleRegisterBiometric} disabled={registeringBiometric || webauthnCreds.length >= 5} className="w-full gap-2 rounded-full" variant={webauthnCreds.length > 0 ? 'outline' : 'default'} data-testid="register-biometric-button">
                <Fingerprint className="h-4 w-4" />
                {webauthnCreds.length >= 5 ? t('profile_biometric_max') : registeringBiometric ? t('profile_biometric_registering') : webauthnCreds.length > 0 ? t('profile_biometric_add') : t('profile_biometric_register')}
              </Button>
              {webauthnCreds.length > 0 && (
                <p className="text-xs text-muted-foreground text-center mt-2">{webauthnCreds.length}/5</p>
              )}
            </div>
          )}

          {/* 2FA Section */}
          <div className="bg-card border border-border/50 rounded-2xl p-6" data-testid="2fa-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary">{t('profile_2fa_title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {twoFAEnabled ? t('profile_2fa_enabled') : t('profile_2fa_disabled')} — {t('profile_2fa_totp')}
                </p>
              </div>
            </div>

            {twoFAEnabled ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm text-emerald-700 font-medium">{t('profile_2fa_active')}</span>
                </div>
                <div className="flex gap-2">
                  <Input type="text" placeholder={t('profile_2fa_enter_code')} value={disableCode} onChange={(e) => setDisableCode(e.target.value)} maxLength={6} className="font-mono" data-testid="disable-2fa-code" />
                  <Button variant="destructive" onClick={handleDisable2FA} disabled={disableCode.length < 6} className="rounded-full shrink-0" data-testid="disable-2fa-button">
                    {t('profile_2fa_disable')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={handleSetup2FA} className="w-full gap-2 rounded-full" data-testid="setup-2fa-button">
                <ShieldCheck className="h-4 w-4" />
                {t('profile_2fa_setup')}
              </Button>
            )}
          </div>
        </div>

        {/* 2FA Setup Dialog */}
        <Dialog open={twoFADialogOpen} onOpenChange={setTwoFADialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('profile_2fa_setup_title')}</DialogTitle>
            </DialogHeader>
            {twoFASetup && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('profile_2fa_scan_desc')}
                </p>
                <div className="flex justify-center p-4 bg-white rounded-xl">
                  <img src={twoFASetup.qr_code} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">{t('profile_2fa_manual')}</p>
                  <code className="text-xs bg-secondary px-3 py-1.5 rounded font-mono select-all" data-testid="2fa-secret">
                    {twoFASetup.secret}
                  </code>
                </div>
                <div className="space-y-2">
                  <Label>{t('profile_2fa_code_label')}</Label>
                  <Input type="text" placeholder={t('profile_2fa_code_placeholder')} value={twoFACode} onChange={(e) => setTwoFACode(e.target.value)} maxLength={6} className="text-center text-xl font-mono tracking-widest" data-testid="2fa-verify-code" />
                </div>
                <Button onClick={handleVerify2FA} disabled={twoFACode.length < 6} className="w-full rounded-full" data-testid="2fa-verify-button">
                  {t('profile_2fa_activate')}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
